'use client';

import { useCalendarStore } from '@/stores/calendar-store';
import { uiFb } from '@/lib/i18n';
import { YearSelector } from './YearSelector';
import { ViewSwitcher } from './ViewSwitcher';
import { CalendarExportMenu } from './CalendarExportMenu';
import { CalendarSystemSelector } from './CalendarSystemSelector';
import { CalendarLegend } from './CalendarLegend';
import type { CalendarEvent } from '@/lib/types';

interface CalendarToolbarProps {
  /** Filter area content (CascadingFilter or null) */
  filterContent?: React.ReactNode;
  /** Available years for the year selector */
  availableYears: number[];
  /** Filtered events for export (when provided, shows export menu) */
  events?: CalendarEvent[];
  /** Title for export filenames */
  exportTitle?: string;
  /** Country code for export filename */
  countryCode?: string;
}

export function CalendarToolbar({
  filterContent,
  availableYears,
  events,
  exportTitle,
  countryCode,
}: CalendarToolbarProps) {
  const lang = useCalendarStore((s) => s.lang);

  return (
    <div
      role="toolbar"
      aria-label={uiFb('ui.toolbar', 'Calendar controls', lang)}
      className="rounded-lg md:rounded-xl border border-border-subtle bg-surface-card shadow-xs"
    >
      <div className="px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        {/* Left group: data controls (what you see) */}
        {filterContent}
        {filterContent && availableYears.length > 1 && (
          <div className="h-5 w-px bg-border-subtle shrink-0" aria-hidden="true" />
        )}
        {availableYears.length > 1 && <YearSelector availableYears={availableYears} bare />}

        {/* Spacer */}
        <div className="ml-auto" />

        {/* Right group: presentation controls (how you see it) */}
        <CalendarLegend />
        <div className="h-5 w-px bg-border-subtle shrink-0" aria-hidden="true" />
        <CalendarSystemSelector bare />
        <div className="h-5 w-px bg-border-subtle shrink-0" aria-hidden="true" />
        <ViewSwitcher responsive />
        {events && events.length > 0 && (
          <>
            <div className="h-5 w-px bg-border-subtle shrink-0" aria-hidden="true" />
            <CalendarExportMenu
              events={events}
              title={exportTitle || 'Calendar'}
              countryCode={countryCode}
            />
          </>
        )}
      </div>
    </div>
  );
}
